/* eslint-disable no-nested-ternary */
import { Box, SxProps, TextField, TextFieldProps } from '@mui/material';
import { ReactNode, useCallback } from 'react';
import { Controller, FieldValues, useFormContext, UseFormResetField } from 'react-hook-form';
import { RenderInput } from 'ui-component/RecordView/components';
import { FieldDataTypeEnum, IRecordField, IRecordFieldsList, IRecordHeaders } from 'ui-component/records/types';
import { ListValue } from 'views/backoffice/CustomLists/types';
import { ACKFieldDropdown } from 'ui-component/formComponents/ACKFieldDropdown';
import { getAditionalFieldsValues } from 'ui-component/RecordView/PropertiesPanel/utils';
import { splitHeaders } from 'ui-component/records/utils/headerHelpers';
import { getFieldName } from 'ui-component/records/utils';
import { splitObjectValues } from 'views/Portal/utils';

export type FormInputProps =
    | {
          label: ReactNode | string;
          sx?: SxProps;
          isRequired?: boolean;
          noWrap?: boolean;
          name?: string;
          activeDataFocusView?: boolean;
          loadingListValues?: boolean;
          defaultValue?: any;
          options?: any[];
          fieldDef?: IRecordField;
          inputRefs?: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
          showFieldMenu?: boolean;
          recordId?: string;
          recordTypeId?: string;
          ACKFieldConfiguration?: {
              isACKField: boolean;
              recordTypeId: number;
              fieldTargetId: string;
              headers?: Record<string, IRecordField>;
          };
      } & (
          | { isFormField?: false | undefined; formComponent: any; disabled?: boolean }
          | {
                name: string;
                isFormField: true;
                fieldDef?: IRecordField;
                disabled?: boolean;
                isCustomList?: boolean;
                resetField?: UseFormResetField<FieldValues>;
                defaultValue?: any;
                isObjectProperty?: boolean;
                inputRefs?: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
                onClear?: () => void;
                labelProcessingFunc?: (option: ListValue) => string;
            }
      );

enum RequiredStatuses {
    required = 'required'
}

export const DATA_TYPES_WITH_NEEDED_OPTIONS_TO_LOAD = ['multiselect-checkbox', 'single-select'] as FieldDataTypeEnum[];

export const FormInlineInput = ({
    label,
    sx,
    isRequired,
    noWrap,
    activeDataFocusView,
    loadingListValues,
    defaultValue,
    inputRefs,
    showFieldMenu,
    recordId,
    recordTypeId,
    ACKFieldConfiguration,
    ...props
}: FormInputProps) => {
    const { setValue, control, resetField, clearErrors, formState, getValues, trigger } = useFormContext();

    const errors = Object.fromEntries(Object.entries(formState.errors).filter(([_, error]) => error?.type === 'manual'));
    const validationError = props.name && errors?.[props.name]?.type === RequiredStatuses.required;
    const isDisabled = props.disabled || !props.fieldDef?.gridEditable;
    const required = (!isDisabled && (isRequired || validationError)) || false;

    // Handle record selection to populate form fields
    // Only populates additional fields, not base fields
    const handleRecordSelect = useCallback(
        (record: IRecordHeaders) => {
            if (!ACKFieldConfiguration?.headers || !record) return;

            // Store the selected record ID and recordTypeId in form context for line items loading
            // Use special field names that won't conflict with actual form fields
            if (record.id) {
                setValue('__selectedAckRecordId__', Number(record.id), { shouldValidate: false });
            }
            if (ACKFieldConfiguration.recordTypeId) {
                setValue('__selectedAckRecordTypeId__', ACKFieldConfiguration.recordTypeId, { shouldValidate: false });
            }

            // Get all current form field names
            const currentFormValues = getValues();
            const formFieldNames = new Set(Object.keys(currentFormValues));

            // Split headers to get only additional fields
            const { additionalFieldHeaders } = splitHeaders(ACKFieldConfiguration.headers as IRecordFieldsList);

            // Split object and non-object additional fields
            const { objectFields, nonObjectFields } = splitObjectValues(record.additionalFields || []);

            // Get additional field values (non-object fields)
            const additionalFieldsValues = getAditionalFieldsValues(additionalFieldHeaders, nonObjectFields);

            // Handle object fields
            const objectFieldsValues: Record<string, any> = {};
            for (const field of objectFields) {
                let [, , fieldId] = field.tag.split(';');
                fieldId = fieldId || field.tag.split(':')[1].split(';')[1];
                const key = getFieldName(ACKFieldConfiguration.headers as IRecordFieldsList, fieldId || fieldId);
                if (key !== 'not found' && field.objectValue?.objectValues) {
                    for (const objectValue of field.objectValue.objectValues) {
                        const propertyName = objectValue.objectProperty.name;
                        if (objectFieldsValues[key]) {
                            objectFieldsValues[key][propertyName] = objectValue.value;
                        } else {
                            objectFieldsValues[key] = {
                                [propertyName]: objectValue.value
                            };
                        }
                    }
                }
            }

            // Combine additional fields and object fields
            const allAdditionalFields = { ...additionalFieldsValues, ...objectFieldsValues };

            // Only populate additional fields that exist in the current form
            Object.entries(allAdditionalFields).forEach(([fieldName, value]) => {
                if (formFieldNames.has(fieldName)) {
                    setValue(fieldName, value, { shouldValidate: true });
                }
            });
        },
        [ACKFieldConfiguration, getValues, setValue]
    );

    // Handle clearing the ACK field and its autopopulated fields
    const handleClear = useCallback(() => {
        if (!ACKFieldConfiguration?.headers || !props.name) return;

        // Clear the selected ACK record ID and recordTypeId from form context
        setValue('__selectedAckRecordId__', undefined, { shouldValidate: false });
        setValue('__selectedAckRecordTypeId__', undefined, { shouldValidate: false });

        // Get all current form field names
        const currentFormValues = getValues();
        const formFieldNames = Array.from(Object.keys(currentFormValues));
        const formFieldNamesSet = new Set(formFieldNames);

        // Split headers to get only additional fields
        const { additionalFieldHeaders } = splitHeaders(ACKFieldConfiguration.headers as IRecordFieldsList);

        // Track fields to clear: non-object fields and object property fields (dot notation)
        const fieldsToClear = new Set<string>();
        // Track date/datetime fields that need special handling
        const dateFieldsToClear = new Set<string>();
        // Track object fields that should be cleared as empty objects
        const objectFieldsToClear = new Set<string>();

        // Add non-object additional field names
        Object.keys(additionalFieldHeaders).forEach((fieldName) => {
            if (formFieldNamesSet.has(fieldName)) {
                const fieldDef = additionalFieldHeaders[fieldName];
                // Check if it's a date or datetime or time field
                if (fieldDef?.dataType === 'date' || fieldDef?.dataType === 'datetime' || fieldDef?.dataType === 'time') {
                    dateFieldsToClear.add(fieldName);
                } else {
                    fieldsToClear.add(fieldName);
                }
            }
        });

        // Get object field names from all headers (including additionalFieldHeaders) and their properties
        // Check both the main headers and additionalFieldHeaders to ensure we catch all object fields
        const allHeadersToCheck = { ...ACKFieldConfiguration.headers, ...additionalFieldHeaders };

        // Also check all headers for date fields that might not be in additionalFieldHeaders
        Object.entries(allHeadersToCheck).forEach(([fieldName, fieldDef]) => {
            // Check for date/datetime fields
            if ((fieldDef?.dataType === 'date' || fieldDef?.dataType === 'datetime') && formFieldNamesSet.has(fieldName)) {
                // Only add if not already in dateFieldsToClear (to avoid duplicates)
                if (!dateFieldsToClear.has(fieldName) && !fieldsToClear.has(fieldName)) {
                    dateFieldsToClear.add(fieldName);
                }
            }

            if (fieldDef?.objectDefinition) {
                // Check if this object field exists in the form
                if (formFieldNamesSet.has(fieldName)) {
                    // Check if it's stored as an object (by checking if the value is an object)
                    const currentValue = currentFormValues[fieldName];
                    if (currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)) {
                        // It's stored as an object, clear it as an empty object
                        objectFieldsToClear.add(fieldName);
                    } else {
                        // It might be stored differently, clear as empty string
                        fieldsToClear.add(fieldName);
                    }
                }

                // Find all form fields that start with this field name followed by a dot
                // These are object property fields using dot notation (e.g., "fieldName.propertyName")
                formFieldNames.forEach((formFieldName) => {
                    if (formFieldName.startsWith(`${fieldName}.`)) {
                        // For object properties, we'll add them to fieldsToClear
                        // Note: We could check the property's dataType here if we had access to propertiesById
                        fieldsToClear.add(formFieldName);
                    }
                });
            }
        });

        // Clear all non-object fields and object property fields (dot notation)
        fieldsToClear.forEach((fieldName) => {
            setValue(fieldName, '', { shouldValidate: true });
        });

        // Clear date/datetime/time fields - set to null to trigger DatePicker re-render
        // RenderInput now uses `null` instead of empty string for DatePicker value prop
        // Setting to null will make the DatePicker show as empty and trigger re-render
        // The DatePicker's onChange handler (in RenderInput) converts null to empty string
        dateFieldsToClear.forEach((fieldName) => {
            // Set to null - DatePicker will show as empty when value is null
            // The DatePicker's onChange handler (in RenderInput) converts null to empty string
            setValue(fieldName, null, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
            // Trigger the field's onChange and validation to ensure the form handles the change
            trigger(fieldName);
            // Also clear any validation errors
            clearErrors?.(fieldName);
        });

        // Clear object fields as empty objects
        objectFieldsToClear.forEach((fieldName) => {
            setValue(fieldName, {}, { shouldValidate: true });
        });
    }, [ACKFieldConfiguration?.headers, props.name, getValues, setValue, trigger, clearErrors]);

    // Render ACK Field Dropdown if configured
    if (ACKFieldConfiguration?.isACKField && props.isFormField && props.name) {
        return (
            <Box
                ref={(el: HTMLDivElement | null) => {
                    if (inputRefs?.current && props.name) {
                        inputRefs.current[props.name] = el;
                    }
                }}
                data-testid="ack-field-inline-input"
                sx={{ width: '100%', height: '100%', px: '0.5rem' }}
            >
                <Controller
                    name={props.name}
                    control={control}
                    defaultValue={defaultValue || ''}
                    render={({ field }) => (
                        <ACKFieldDropdown
                            recordTypeId={ACKFieldConfiguration.recordTypeId}
                            fieldTargetId={ACKFieldConfiguration.fieldTargetId}
                            selectedId={field.value}
                            defaultValue={defaultValue}
                            onChange={(value) => {
                                field.onChange(value);
                                setValue(props.name, value, { shouldValidate: true });
                            }}
                            onRecordSelect={handleRecordSelect}
                            error={!!validationError}
                            helperText={validationError ? 'Required Field' : undefined}
                            disabled={isDisabled}
                            name={props.name}
                            onClear={() => {
                                field.onChange('');
                                handleClear();
                            }}
                        />
                    )}
                />
            </Box>
        );
    }

    const inputComponent = props.isFormField ? (
        props.fieldDef ? (
            <RenderInput
                control={control}
                options={props.options}
                isCustomList={props.isCustomList}
                errors={errors}
                fieldData={props.fieldDef}
                name={props.name}
                label={label as string}
                disabled={props.disabled}
                hideTitle
                backgroundAlways
                defaultValue={defaultValue}
                isObjectProperty={props.isObjectProperty}
                setValue={setValue}
                isRequired={required}
                resetField={resetField}
                TextFieldComponentProps={
                    {
                        onClear:
                            !props.disabled && !['single-select', 'dropdown', 'dropDown'].includes(props.fieldDef.dataType)
                                ? () => setValue(props.name, '', { shouldValidate: true })
                                : undefined
                    } as TextFieldProps
                }
                loadingListValues={loadingListValues}
                clearErrors={clearErrors}
                showFieldMenu={showFieldMenu}
                recordId={recordId}
                recordTypeId={recordTypeId}
            />
        ) : (
            <TextField
                variant="filled"
                size="small"
                InputProps={{ hiddenLabel: true, disableUnderline: true, sx: { borderRadius: '8px' } }}
                fullWidth
            />
        )
    ) : (
        props.formComponent
    );

    return (
        <Box
            ref={(el: HTMLDivElement | null) => {
                if (inputRefs?.current && props.name) {
                    inputRefs.current[props.name] = el;
                }
            }}
            sx={{ width: '100%', height: '100%', px: '0.5rem' }}
        >
            {inputComponent}
        </Box>
    );
};
